/**
 * @fileoverview useStepHydration — Full lifecycle hook for ingestion steps.
 *
 * Owns: form creation, API hydration, import hydration, assisted field detection.
 *
 * `isAssisted(fieldName)` is pure derivation: "does the import have a present
 * value for this field, and does the current form value match it?"
 * No tracking, no refs, no session state.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import type { ZodType } from 'zod';

import type { ImportedField } from '@/services/types/case-ingestion-assist-types';

// ── Types ────────────────────────────────────────────────────────────────────

export type ImportFieldMap<T extends FieldValues> = Partial<
  Record<keyof T, ImportedField<unknown>>
>;

interface UseStepHydrationOptions<T extends FieldValues> {
  schema: ZodType<T>;
  defaults: T;
  /** Saved data in form shape. null while loading. */
  saved: Partial<T> | null;
  /** Import data mapped to ImportedField wrappers. null if no import. */
  imported: ImportFieldMap<T> | null;
  /**
   * Custom comparators for specific fields. Keyed by field name.
   * When provided, used instead of the default valuesMatch for that field.
   */
  comparators?: Partial<Record<keyof T, (imported: unknown, current: unknown) => boolean>>;
  /**
   * Custom merge strategy for import hydration. Default: fill empty fields.
   * Called once after saved data hydration.
   */
  merge?: (form: UseFormReturn<T>, imported: ImportFieldMap<T>) => void;
  /**
   * Extra assisted field mappings for entity lookups where the form value (ID)
   * differs from the imported value (name). Maps fieldName → current form value
   * that should be considered "assisted".
   */
  assistedOverrides?: Record<string, unknown>;
}

interface UseStepHydrationResult<T extends FieldValues> {
  form: UseFormReturn<T>;
  /**
   * Returns true when the import has a present value for this field
   * AND the current form value matches the imported value (or an override).
   */
  isAssisted: (fieldName: string) => boolean;
  isHydrating: boolean;
}

// ── Default merge ────────────────────────────────────────────────────────────

function defaultMerge<T extends FieldValues>(
  form: UseFormReturn<T>,
  imported: ImportFieldMap<T>,
): void {
  const { setValue, getValues } = form;

  Object.entries(imported).forEach(([key, field]) => {
    const importedField = field as ImportedField<unknown> | undefined;

    if (!importedField || importedField.presence !== 'present' || importedField.value == null) {
      return;
    }

    const currentValue = getValues(key as Path<T>);

    if (isEmpty(currentValue)) {
      setValue(key as Path<T>, importedField.value as T[keyof T & string]);
    }
  });
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useStepHydration<T extends FieldValues>(
  options: UseStepHydrationOptions<T>,
): UseStepHydrationResult<T> {
  const {
    schema,
    defaults,
    saved,
    imported,
    merge = defaultMerge,
    assistedOverrides,
    comparators,
  } = options;

  const form = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues: defaults as never,
  });

  const { reset, getValues } = form;
  const hasHydratedSaved = useRef(false);
  const hasHydratedImport = useRef(false);
  // Bump to force re-render after hydration effects complete
  const [, setHydrationTick] = useState(0);

  // Phase 1: Saved data → reset()
  useEffect(() => {
    if (!saved || hasHydratedSaved.current) return;
    hasHydratedSaved.current = true;
    reset({ ...getValues(), ...saved } as T);
    setHydrationTick((t) => t + 1);
  }, [saved, reset, getValues]);

  // Phase 2: Import → fill empties (once, after saved)
  // Depends on `saved` so it re-evaluates when Phase 1 completes
  useEffect(() => {
    if (!hasHydratedSaved.current) return;
    if (!imported || hasHydratedImport.current) return;
    hasHydratedImport.current = true;
    merge(form, imported);
    // Force re-render so isAssisted picks up the new values
    setHydrationTick((t) => t + 1);
  }, [imported, merge, form, saved]);

  // Force re-render when form values change so isAssisted re-evaluates
  // useWatch returns a proxy that triggers re-render on value changes
  useWatch({ control: form.control });

  const isAssisted = (fieldName: string): boolean => {
    // Read current value directly from form (always fresh)
    const currentVal = form.getValues(fieldName as Path<T>);

    // Check overrides first (for entity lookups like attorney/case manager)
    if (assistedOverrides && fieldName in assistedOverrides) {
      const overrideVal = assistedOverrides[fieldName];
      return overrideVal != null && currentVal != null && overrideVal === currentVal;
    }

    // Standard check: import has present value AND current form matches it
    if (!imported) return false;
    const field = (imported as Record<string, ImportedField<unknown>>)[fieldName];
    if (!field || field.presence !== 'present' || field.value == null) return false;
    const cmp = comparators?.[fieldName as keyof T];
    return cmp ? cmp(field.value, currentVal) : valuesMatch(field.value, currentVal);
  };

  return {
    form,
    isAssisted,
    isHydrating: !hasHydratedSaved.current && !saved,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function valuesMatch(imported: unknown, current: unknown): boolean {
  const norm = (v: unknown) => (v === null || v === undefined ? '' : v);
  const a = norm(imported);
  const b = norm(current);
  if (typeof a === 'string' && typeof b === 'string') return a === b;
  // For objects/arrays, compare with a normalizing JSON approach
  // that strips undefined values and extra keys
  try {
    return normalizedJson(a) === normalizedJson(b);
  } catch {
    return a === b;
  }
}

/** JSON.stringify that strips undefined values and sorts keys for stable comparison */
function normalizedJson(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val === undefined) {
      return undefined;
    }

    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val)
        .sort()
        .reduce<Record<string, unknown>>((sorted, key) => {
          const entryValue = (val as Record<string, unknown>)[key];

          if (entryValue !== undefined) {
            sorted[key] = entryValue;
          }

          return sorted;
        }, {});
    }

    return val;
  });
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every((item) => isEmpty(item));
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).every((v) => isEmpty(v));
  }
  return false;
}
